<template>
  <v-container>
    <!-- page header -->
    <v-row>
      <v-col cols="12">
        <v-row>
          <v-col cols="12" md="8">
            <h3 class="display-1">User Accounts</h3>
            <p class="mt-2">Total User Accounts: {{ userAccounts.length }}</p>
          </v-col>
          <v-col cols="12" md="4" align="end">
            <v-btn color="primary" depressed to="/">Home</v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- userAccounts table -->
    <v-row>
      <v-col cols="12">
        <v-card outlined>
          <v-card-text class="text--primary">
            <v-row>
              <v-col cols="12" md="7"></v-col>
              <v-col cols="8" md="5">
                <v-text-field
                  label="Search Users"
                  dense
                  clearable
                  outlined
                  hide-details
                  v-model="search"
                  prepend-inner-icon="mdi-magnify"
                />
              </v-col>
            </v-row>
          </v-card-text>

          <v-data-table
            dense
            :headers="headers"
            :items="userAccounts"
            :items-per-page="-1"
            :loading="loading"
            :search="search"
            @click:row="onViewUser"
          >
            <template v-slot:[`item.dateRegistered`]="{ item }">
              {{ formatDate(item.dateRegistered) }}
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { portalsApp } from "@/firebase";
import formatDate from "@/mixins/formatDate";
import { can, getRole } from "@/services/auth";

export default {
  mixins: [formatDate],

  data: () => ({
    search: "",
    userAccountsAll: [],
    userAccountsAssigned: [],
    userAccountsAssumed: [],
    headers: [
      { text: "Last Name", value: "lastName" },
      { text: "Email", value: "email" },
      { text: "Secondary Email", value: "secondaryEmail" },
      { text: "PUID", value: "packageID" },
      { text: "Date Registered", value: "dateRegistered" },
    ],
  }),

  computed: {
    loading() {
      return this.$store.state.loading;
    },

    currentUser() {
      return this.$store.state.user || null;
    },

    currentUid() {
      return this.currentUser && this.currentUser.uid
        ? this.currentUser.uid
        : "";
    },

    role() {
      return getRole(this.currentUser, this.$store);
    },

    canViewAllUsers() {
      return this.role === "superAdmin" || this.role === "management";
    },

    canReassignPuid() {
      return can(this.currentUser, this.$store, "reassignPuid");
    },

    userAccounts() {
      if (this.canViewAllUsers) return this.userAccountsAll || [];

      var map = new Map();
      (this.userAccountsAssigned || []).forEach(function(u) {
        map.set(u.id, u);
      });
      (this.userAccountsAssumed || []).forEach(function(u) {
        map.set(u.id, u);
      });

      var arr = Array.from(map.values());

      // No optional chaining - compatible with older Vue/Babel setups
      arr.sort(function(a, b) {
        var ad =
          a && a.dateRegistered && a.dateRegistered.toMillis
            ? a.dateRegistered.toMillis()
            : a && a.dateRegistered
            ? a.dateRegistered
            : 0;

        var bd =
          b && b.dateRegistered && b.dateRegistered.toMillis
            ? b.dateRegistered.toMillis()
            : b && b.dateRegistered
            ? b.dateRegistered
            : 0;

        return bd - ad;
      });

      return arr;
    },
  },

  async mounted() {
    await this.rebindUsers();
  },

  watch: {
    currentUid() {
      this.rebindUsers();
    },
    role() {
      this.rebindUsers();
    },
  },

  methods: {
    async rebindUsers() {
      await this.$store.dispatch("setLoading", true);

      function safeUnbind(vm, key) {
        if (
          vm &&
          vm._firestoreUnbinds &&
          typeof vm._firestoreUnbinds[key] === "function"
        ) {
          vm.$unbind(key);
        }
      }
      safeUnbind(this, "userAccountsAll");
      safeUnbind(this, "userAccountsAssigned");
      safeUnbind(this, "userAccountsAssumed");

      var col = portalsApp.firestore().collection("userAccounts");

      if (this.canViewAllUsers) {
        var qAll = col.orderBy("dateRegistered", "desc");
        await this.$bind("userAccountsAll", qAll);
        await this.$store.dispatch("setLoading", false);
        return;
      }

      if (!this.currentUid) {
        await this.$store.dispatch("setLoading", false);
        return;
      }

      var qAssigned = col.where("assignedToUid", "==", this.currentUid);

      var qAssumed = col.where("assumedByUid", "==", this.currentUid);

      await this.$bind("userAccountsAssigned", qAssigned);
      await this.$bind("userAccountsAssumed", qAssumed);

      await this.$store.dispatch("setLoading", false);
    },

    onViewUser(user) {
      this.$router.push(`users/${user.id}`);
    },
  },
};
</script>

<style>
tr {
  cursor: pointer !important;
}
</style>
